import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

// Clause redline: old terms strike out, revised terms insert, then the clause
// settles into a clean copy. Namespaced (clr-*), palette-mapped, reduced-motion
// safe (jumps straight to clean copy).
const SEG = [
  ["The Purchaser shall pay the balance consideration within ", "s"],
  ["thirty", "d"],
  ["sixty", "i"],
  [" days from the date of ", "s"],
  ["execution", "d"],
  ["registration", "i"],
  [" of the sale deed", "s"],
  [", failing which interest accrues at 12% per annum", "i"],
  [".", "s"],
];

const CSS = `
.clr-wrap{--ink:#20242A;--mute:#606773;--ok:#4F7A62;--bad:#A85353;width:100%}
.clr-box{width:100%;padding:16px;border:.75px solid var(--ink);border-radius:6px;background:#FBFAF8;font-family:Georgia,"Cormorant Garamond",serif;font-size:15px;line-height:1.7;color:var(--ink)}
.clr-d,.clr-i{display:inline-block;white-space:pre;overflow:hidden;vertical-align:bottom;max-width:30em;transition:max-width .9s ease,opacity .9s ease,color .5s}
.clr-d.clr-x{color:var(--bad);text-decoration:line-through}
.clr-i{max-width:0;opacity:0}
.clr-i.clr-show{max-width:30em;opacity:1;color:var(--ok);border-bottom:1px solid var(--ok)}
.clr-d.clr-gone{max-width:0;opacity:0}
.clr-i.clr-final{color:var(--ink);border-bottom-color:transparent}
.clr-status{margin:8px 0 0;color:var(--mute);text-align:center;font-family:Inter,sans-serif;font-size:12px}
`;

export function ClauseRedlineMark({ className }) {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const ds = [...root.querySelectorAll(".clr-d")];
    const is = [...root.querySelectorAll(".clr-i")];
    const status = root.querySelector(".clr-status");
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    if (reduce) {
      ds.forEach((e) => (e.className = "clr-d clr-gone"));
      is.forEach((e) => (e.className = "clr-i clr-show clr-final"));
      status.textContent = "Clean copy";
      return;
    }
    let cancelled = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const reset = () => { ds.forEach((e) => (e.className = "clr-d")); is.forEach((e) => (e.className = "clr-i")); status.textContent = "Original draft"; };
    (async () => {
      while (!cancelled) {
        reset(); await sleep(1800); if (cancelled) break;
        status.textContent = "Striking out the old terms";
        for (const e of ds) { e.classList.add("clr-x"); await sleep(500); if (cancelled) return; }
        await sleep(600);
        status.textContent = "Inserting the revised terms";
        for (const e of is) { e.classList.add("clr-show"); await sleep(700); if (cancelled) return; }
        await sleep(1600);
        status.textContent = "Clean copy";
        ds.forEach((e) => e.classList.add("clr-gone"));
        is.forEach((e) => e.classList.add("clr-final"));
        await sleep(4200);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div ref={ref} className={cn("clr-wrap", className)} data-testid="clause-redline-mark">
      <style>{CSS}</style>
      <div className="clr-box">
        <span>
          {SEG.map(([w, k], i) => (
            <span key={i} className={k === "d" ? "clr-d" : k === "i" ? "clr-i" : undefined}>{w}</span>
          ))}
        </span>
      </div>
      <p className="clr-status" />
    </div>
  );
}
